import { type CompanyProfileMainContentV2_organization$key } from '#app/__generated__/relay/CompanyProfileMainContentV2_organization.graphql.js';
import { CompanyOverviewTab } from '#app/components/companyProfilesV2/components/CompanyOverviewTab.js';
import { CompanyTabId } from '#app/components/companyProfilesV2/types.js';
import {
  buildTabs,
  getCompanyRouteKeyFromTabId,
  getTabIdFromView,
} from '#app/components/companyProfilesV2/utilities.js';
import { NavigationTabs } from '#app/components/NavigationTabs/NavigationTabs.js';
import { NewWorkOpportunitySkeleton } from '#app/components/SkeletonLoaders/NewWorkOpportunitySkeleton.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { usePageContext } from '#app/hooks/usePageContext.js';
import { CompanyLists } from '../CompanyLists/CompanyLists.js';
import { CompanyOpenOpportunityListV2 } from '../CompanyOpenOpportunityList/CompanyOpenOpportunityList.js';
import { CompanyPeopleList } from '../CompanyPeopleList/CompanyPeopleList.js';
import { CompanyRecommendationsList } from '../CompanyRecommendations/CompanyRecommendationsList.js';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { Suspense, useCallback } from 'react';
import { css, styled } from 'styled-components';

const Container = styled.div`
  width: 100%;
  display: block;

  margin-top: 24px;
  margin-bottom: 80px;

  ${({ theme }) => css`
    ${theme.mediaQueries.lg} {
      margin-top: 56px;
      margin-bottom: 0;
    }
  `}
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  margin-top: 12px;

  ${({ theme }) => css`
    ${theme.mediaQueries.md} {
      margin-top: 16px;
    }
  `}
`;

type MainContentProps = {
  readonly organizationRef: CompanyProfileMainContentV2_organization$key;
};

export const CompanyProfileMainContentV2 = ({
  organizationRef,
}: MainContentProps) => {
  const data = useFragment<CompanyProfileMainContentV2_organization$key>(
    graphql`
      fragment CompanyProfileMainContentV2_organization on Organization {
        id
        visitorIsMember
        userLists {
          count
        }
        ...CompanyOpenOpportunityListV2_organization
        ...CompanyRecommendationsList_organization
        ...CompanyPeopleList_organization
        ...CompanyOverviewTab_organization
        ...CompanyLists_organization
      }
    `,
    organizationRef,
  );

  const pageContext = usePageContext();
  const slug = pageContext.routeParams.slug;
  const view = getTabIdFromView(pageContext.routeParams.view);
  const navigation = useNavigation();

  const handleClick = useCallback(
    (tabId: CompanyTabId) => {
      if (tabId !== view) {
        navigation.push(
          getRoute(getCompanyRouteKeyFromTabId(tabId), {
            args: {
              slug,
            },
          }),
        );
      }
    },
    [navigation, slug, view],
  );

  const showNetworkLists = data.userLists.count > 0 || data.visitorIsMember;

  const companyTabs = buildTabs(data.visitorIsMember, showNetworkLists);

  const primaryView = (
    <>
      {view === 'jobs' && (
        <Suspense fallback={<NewWorkOpportunitySkeleton />}>
          <CompanyOpenOpportunityListV2 organizationRef={data} />
        </Suspense>
      )}
      {view === 'recommendations' && (
        <CompanyRecommendationsList organizationRef={data} />
      )}
      {view === 'people' && data.visitorIsMember && (
        <CompanyPeopleList
          inviteTeamMembersCTA
          organizationRef={data}
        />
      )}

      {view === 'lists' && showNetworkLists && (
        <Suspense fallback={<NewWorkOpportunitySkeleton />}>
          <CompanyLists organizationRef={data} />
        </Suspense>
      )}

      {view === 'overview' && <CompanyOverviewTab organizationRef={data} />}
    </>
  );

  return (
    <Container>
      <NavigationTabs<CompanyTabId>
        activeTabId={view}
        onTabClick={handleClick}
        tabs={companyTabs}
      />
      <MainContent>{primaryView}</MainContent>
    </Container>
  );
};
